import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCode,
  faDesktop,
  faEllipsisVertical,
  faEye,
  faFloppyDisk,
  faMobile,
  faPaperPlane,
  faRotateLeft,
  faRotateRight,
  faText,
  faTrash,
} from "@fortawesome/pro-regular-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import classNames from "classnames";
import { ButtonNew as Button } from "components/buttonNew";
import { Badge } from "components/badge/Badge";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import Spin from "components/spin/spin";
import { PostEmailCampaignDraftExport, PostEmailCampaignDesignPublish, PostEmailCampaignDraftDiscard } from "api/rpc/2024-04/facilityAdmin/marketing/emailBuilder";
import { GetEmailCampaigns } from "api/rpc/2024-04/facilityAdmin/marketing/emailCampaigns";
import { StatusCode } from "api/protocols";
import {
  PostEmailMarketingTemplateActivate,
  PostEmailMarketingTemplateDraftDiscard,
  PostEmailMarketingTemplateExport,
} from "api/rpc/2024-04/facilityAdmin/marketing/emailMarketingTemplates";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import type { RootState } from "index";
import { useStore } from "react-redux";
import {
  emailBuilderCancelPendingAutosave,
  emailBuilderLoad,
  emailBuilderSetCampaignName,
  emailBuilderSetMode,
  emailBuilderSetPreviewViewport,
  emailBuilderTemplateActivated,
  emailBuilderUpdateAutosaveStatus,
} from "redux/actions/emailBuilder/emailBuilder";
import { EmailBuilderActionType } from "redux/actions/emailBuilder/emailBuilderAction";
import { emailBuilderLoadedStateFromTemplate } from "redux/actions/emailBuilder/emailBuilderLoadedStateFromDraft";
import type {
  TEmailBuilderAutoSaveStatus,
  TEmailBuilderPreviewViewport,
} from "redux/reducers/emailBuilder/emailBuilder";
import { EMAIL_BUILDER_AUTOSAVE_CONFLICT_MESSAGE } from "redux/reducers/emailBuilder/emailBuilderAutosave";
import { showError, showSuccess } from "redux/actions/ui";
import { useEmailBuilderViewport } from "../emailBuilderViewportContext";
import { useHistory, useLocation } from "react-router";
import { emailBuilderCommitAndFlushDraft, emailBuilderCommitActiveEdits } from "../../emailBuilderCommitAndFlushDraft";
import {
  emailCampaignSelectTemplatePath,
  emailTemplateLibraryPath,
  resolveEmailBuilderReturnTo,
} from "../../emailBuilderWorkspace";
import {
  emailBuilderDraftStatusAnnouncement,
  emailBuilderDraftStatusBadgeType,
  emailBuilderDraftStatusLabel,
  emailBuilderTemplateStatusKind,
  emailBuilderUnsavedCountdownSeconds,
} from "../emailBuilderDraftStatusLabel";
import {
  emailBuilderHistoryHotkeyShortcutLabels,
  emailBuilderIsMacPlatform,
} from "../emailBuilderHistoryHotkeys";
import { downloadEmailBuilderExportHtml } from "../export/emailBuilderExportHtml";
import { selectFutureCount, selectPastCount } from "../../domain/selectors";
import { TopAlert } from "components/topAlert/TopAlert";
import { EmailBuilderSaveAsTemplateModal } from "../../saveAsTemplate/EmailBuilderSaveAsTemplateModal";
import { EmailBuilderTemplateLibraryRenameModal } from "../../templateLibrary/EmailBuilderTemplateLibraryNameModal";
import { EmailBuilderSendTestSheet } from "../sendTest/EmailBuilderSendTestSheet";
import { EmailBuilderDiscardDraftModal } from "./EmailBuilderDiscardDraftModal";
import { EmailBuilderFinalizeConfirmModal } from "./EmailBuilderFinalizeConfirmModal";
import { scanEmailBuilderFinalizeReview } from "../../operations/emailBuilderPublishLinkScan";
import { emailBuilderApiErrorMessage } from "../../operations/emailBuilderApiErrorMessage";
import { translateEmailBuilderValidationMessage } from "../../operations/translateEmailBuilderValidationMessage";
import type { IEmailBuilderFinalizeReview } from "../../operations/emailBuilderPublishLinkScan";
import { resolveEmailCampaignSchedulePathFromReturnTo } from "../../../emailCampaigns/emailCampaignScheduleState";
import { isEmailCampaignDetailsReadyForSchedule } from "../../../emailCampaigns/emailCampaignDesignStatus";
import { EMAIL_BUILDER_Z_LAYERS } from "../emailBuilderShellZLayers";
import { useEditorActions, useEditorStore } from "../../domain/redux/hooks";
import { selectionForBlockId } from "../../domain/history-reveal";
import { scheduleFocusAndRevealSelection } from "../../domain/reveal-selection";
import { getEditorStoreState } from "../../domain/store";
import { EmailBuilderSkeleton } from "../EmailBuilderSkeleton";
import "./emailBuilderBanner.scss";

interface IEmailBuilderBannerProps {
  onBack: () => void;
  onMoreMenuOpenChange?: (open: boolean) => void;
}

interface IEmailBuilderPreviewViewportOption {
  viewport: TEmailBuilderPreviewViewport;
  tooltip: string;
}

function useEmailBuilderAutosaveNow(dueAt: number | null): number {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (dueAt === null) {
      return;
    }

    setNowMs(Date.now());
    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [dueAt]);

  return nowMs;
}

export function EmailBuilderBanner(props: IEmailBuilderBannerProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const history = useHistory();
  const location = useLocation();
  const { isMobileLayout: isMobile } = useEmailBuilderViewport();
  const [sendTestOpen, setSendTestOpen] = useState(false);
  const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);
  const [renameTemplateOpen, setRenameTemplateOpen] = useState(false);
  const [discardDraftOpen, setDiscardDraftOpen] = useState(false);
  const [templateSavedAlertOpen, setTemplateSavedAlertOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [publishingDesign, setPublishingDesign] = useState(false);
  const [discardingDraft, setDiscardingDraft] = useState(false);
  const [discardingTemplateDraft, setDiscardingTemplateDraft] = useState(false);
  const [finalizeConfirmOpen, setFinalizeConfirmOpen] = useState(false);
  const [finalizeReview, setFinalizeReview] = useState<IEmailBuilderFinalizeReview>({
    issues: [],
  });
  const [finalizePendingAction, setFinalizePendingAction] = useState<
    "finalizeDesign" | "activateTemplate" | null
  >(null);
  const [liveAnnouncement, setLiveAnnouncement] = useState("");
  const previousAutoSaveStatusRef = useRef<TEmailBuilderAutoSaveStatus | null>(null);
  const isFlushingServerDraftRef = useRef(false);

  const workspace = useAppSelector(store => store.emailBuilderStore.workspace);
  const loadStatus = useAppSelector(store => store.emailBuilderStore.loadStatus);
  const campaign = useAppSelector(store => store.emailBuilderStore.campaign);
  const campaignDesign = useAppSelector(store => store.emailBuilderStore.campaignDesign);
  const templateActivation = useAppSelector(store => store.emailBuilderStore.templateActivation);
  const draft = useAppSelector(store => store.emailBuilderStore.draft);
  const document = useAppSelector(store => store.emailBuilderStore.document);
  const autoSaveStatus = useAppSelector(store => store.emailBuilderStore.autoSaveStatus);
  const autoSaveError = useAppSelector(store => store.emailBuilderStore.autoSaveError);
  const autoSaveConflict = useAppSelector(store => store.emailBuilderStore.autoSaveConflict);
  const autoSaveDueAt = useAppSelector(store => store.emailBuilderStore.autoSaveDueAt);
  const mode = useAppSelector(store => store.emailBuilderStore.mode);
  const previewViewport = useAppSelector(store => store.emailBuilderStore.previewViewport);
  const pastCount = useEditorStore(selectPastCount);
  const futureCount = useEditorStore(selectFutureCount);
  const { undo: editorUndo, redo: editorRedo, setSelection: editorSetSelection } = useEditorActions();

  const canUndo = pastCount > 0;
  const canRedo = futureCount > 0;

  function handleUndo() {
    editorUndo();
  }

  function handleRedo() {
    editorRedo();
  }
  const isTemplateWorkspace = workspace?.kind === "template";
  const ownerId = workspace?.id ?? null;
  const nowMs = useEmailBuilderAutosaveNow(autoSaveDueAt);
  const draftStatusDesignOptions = useMemo(
    () => ({
      design: isTemplateWorkspace ? null : campaignDesign,
      draftRevision: draft?.revision ?? null,
      templateActivation: isTemplateWorkspace ? templateActivation : undefined,
      document: isTemplateWorkspace ? document : undefined,
    }),
    [campaignDesign, document, draft?.revision, isTemplateWorkspace, templateActivation],
  );
  const draftStatusLabel = emailBuilderDraftStatusLabel(autoSaveStatus, {
    dueAt: autoSaveDueAt,
    now: nowMs,
    conflict: autoSaveConflict,
    ...draftStatusDesignOptions,
  });
  const templateStatusKind = emailBuilderTemplateStatusKind(
    templateActivation,
    draft?.revision ?? null,
    document,
  );
  const draftStatusBadgeType = emailBuilderDraftStatusBadgeType(
    autoSaveStatus,
    draftStatusDesignOptions,
  );
  const shortcutLabels = emailBuilderHistoryHotkeyShortcutLabels(
    emailBuilderIsMacPlatform(window.navigator.userAgent),
  );

  useEffect(() => {
    if (previousAutoSaveStatusRef.current === null) {
      previousAutoSaveStatusRef.current = autoSaveStatus;
      return;
    }

    previousAutoSaveStatusRef.current = autoSaveStatus;
    const announcement = emailBuilderDraftStatusAnnouncement(
      autoSaveStatus,
      autoSaveError,
      draftStatusDesignOptions,
    );
    setLiveAnnouncement(
      autoSaveStatus === "error" && autoSaveError !== null && autoSaveError.length > 0
        ? t(autoSaveError)
        : t(announcement),
    );
  }, [autoSaveError, autoSaveStatus, draftStatusDesignOptions]);

  const previewViewportOptions: IEmailBuilderPreviewViewportOption[] = [
    {
      viewport: "desktop",
      tooltip: t("Desktop"),
    },
    {
      viewport: "mobile",
      tooltip: t("Mobile"),
    },
  ];

  async function ensureSavedDraftForServerAction(): Promise<boolean> {
    const result = await emailBuilderCommitAndFlushDraft({
      dispatch,
      getAutoSaveStatus: () => store.getState().emailBuilderStore.autoSaveStatus,
      getAutoSaveConflict: () => store.getState().emailBuilderStore.autoSaveConflict,
      isFlushing: isFlushingServerDraftRef.current,
      setFlushing: flushing => {
        isFlushingServerDraftRef.current = flushing;
      },
    });

    return result === "ok";
  }

  function dispatchDraftRevisionConflict() {
    dispatch(
      emailBuilderUpdateAutosaveStatus(
        "error",
        EMAIL_BUILDER_AUTOSAVE_CONFLICT_MESSAGE,
        true,
        null,
      ),
    );
  }

  function handleBackClick() {
    if (mode === "preview") {
      dispatch(emailBuilderSetMode("design"));
      return;
    }

    props.onBack();
  }

  async function handlePreviewToggle() {
    if (mode === "preview") {
      dispatch(emailBuilderSetMode("design"));
      return;
    }

    if (ownerId === null || workspace === null) {
      return;
    }

    const saved = await ensureSavedDraftForServerAction();
    if (!saved) {
      return;
    }

    dispatch(emailBuilderSetMode("preview"));
  }

  async function handleExportHtml() {
    if (exporting) {
      return;
    }

    setExporting(true);

    try {
      if (ownerId === null || workspace === null) {
        return;
      }

      const saved = await ensureSavedDraftForServerAction();
      if (!saved) {
        return;
      }

      let html: string;
      let filename: string;

      if (workspace.kind === "template") {
        const res = await PostEmailMarketingTemplateExport(ownerId, false);
        if (res.status !== StatusCode.OK || res.data?.data == null) {
          dispatch(
            showError(
              translateEmailBuilderValidationMessage(
                emailBuilderApiErrorMessage(res.message, t("Could not export email HTML."), document),
                t,
              ),
            ),
          );
          return;
        }

        html = res.data.data.html;
        filename = res.data.data.filename;
      } else {
        const res = await PostEmailCampaignDraftExport(ownerId, false);
        if (res.status !== StatusCode.OK || res.data?.data == null) {
          dispatch(
            showError(
              translateEmailBuilderValidationMessage(
                emailBuilderApiErrorMessage(res.message, t("Could not export email HTML."), document),
                t,
              ),
            ),
          );
          return;
        }

        html = res.data.data.html;
        filename = res.data.data.filename;
      }

      const download = downloadEmailBuilderExportHtml(html, filename);

      if (download.ok === false) {
        dispatch(showError(t(download.error)));
        return;
      }

      dispatch(showSuccess(t("HTML exported.")));
    } finally {
      setExporting(false);
    }
  }

  function dismissFinalizeConfirm() {
    setFinalizeConfirmOpen(false);
    setFinalizePendingAction(null);
    setFinalizeReview({
      issues: [],
    });
  }

  function handleFinalizeConfirmGoToBlock(blockId: string) {
    if (publishingDesign || savingTemplate) {
      return;
    }

    if (mode === "preview") {
      dispatch(emailBuilderSetMode("design"));
    }

    const selection = selectionForBlockId(getEditorStoreState().document, blockId);
    if (selection !== null) {
      editorSetSelection(selection);
      scheduleFocusAndRevealSelection(selection);
    }
    dismissFinalizeConfirm();
  }

  function runFinalizeReviewGate(action: "finalizeDesign" | "activateTemplate") {
    emailBuilderCommitActiveEdits(dispatch);

    setFinalizeReview(scanEmailBuilderFinalizeReview(getEditorStoreState().document));
    setFinalizePendingAction(action);
    setFinalizeConfirmOpen(true);
  }

  async function finalizeActivateTemplate() {
    if (
      ownerId === null ||
      workspace?.kind !== "template" ||
      savingTemplate ||
      discardingTemplateDraft
    ) {
      return;
    }

    setSavingTemplate(true);

    try {
      const flushResult = await emailBuilderCommitAndFlushDraft({
        dispatch,
        getAutoSaveStatus: () => store.getState().emailBuilderStore.autoSaveStatus,
      getAutoSaveConflict: () => store.getState().emailBuilderStore.autoSaveConflict,
        isFlushing: isFlushingServerDraftRef.current,
        setFlushing: flushing => {
          isFlushingServerDraftRef.current = flushing;
        },
      });

      if (flushResult === "ignored") {
        return;
      }

      if (flushResult === "failed") {
        dispatch(showError(t("Could not save template.")));
        return;
      }

      const latestDraft = store.getState().emailBuilderStore.draft;
      if (latestDraft === null) {
        dispatch(showError(t("Could not save template.")));
        return;
      }

      const res = await PostEmailMarketingTemplateActivate(
        ownerId,
        { revision: latestDraft.revision },
        false,
      );

      if (res.status === StatusCode.CONFLICT) {
        dispatchDraftRevisionConflict();
        return;
      }

      if (res.status !== StatusCode.OK || res.data?.data == null) {
        dispatch(
          showError(
            translateEmailBuilderValidationMessage(
              emailBuilderApiErrorMessage(res.message, t("Could not save template."), document),
              t,
            ),
          ),
        );
        return;
      }

      dismissFinalizeConfirm();
      dispatch(
        emailBuilderTemplateActivated(
          res.data.data.active_source_draft_revision,
          res.data.data.activated_at,
        ),
      );
      setTemplateSavedAlertOpen(true);
    } finally {
      setSavingTemplate(false);
    }
  }

  function handleSaveTemplate() {
    if (
      ownerId === null ||
      workspace?.kind !== "template" ||
      savingTemplate ||
      discardingTemplateDraft
    ) {
      return;
    }

    runFinalizeReviewGate("activateTemplate");
  }

  async function handleDiscardTemplateDraft() {
    if (
      ownerId === null ||
      workspace?.kind !== "template" ||
      discardingTemplateDraft ||
      savingTemplate
    ) {
      return;
    }

    setDiscardingTemplateDraft(true);

    try {
      emailBuilderCommitActiveEdits(dispatch);
      dispatch(emailBuilderCancelPendingAutosave());

      const latestDraft = store.getState().emailBuilderStore.draft;
      if (latestDraft === null) {
        dispatch(showError(t("Could not discard draft.")));
        return;
      }

      const res = await PostEmailMarketingTemplateDraftDiscard(
        ownerId,
        { revision: latestDraft.revision },
        false,
      );

      if (res.status !== StatusCode.OK || res.data?.data == null) {
        dispatch(
          showError(
            translateEmailBuilderValidationMessage(
              emailBuilderApiErrorMessage(res.message, t("Could not discard draft."), document),
              t,
            ),
          ),
        );
        return;
      }

      const loaded = emailBuilderLoadedStateFromTemplate(res.data.data);
      if (loaded.ok === false) {
        dispatch(showError(t(loaded.error)));
        return;
      }

      dispatch({
        type: EmailBuilderActionType.LOAD_SUCCEEDED,
        payload: { ...loaded.payload, campaignDesign: null },
      });
      setDiscardDraftOpen(false);
      dispatch(showSuccess(t("Draft discarded.")));
    } finally {
      setDiscardingTemplateDraft(false);
    }
  }

  async function finalizePublishDesign() {
    if (ownerId === null || workspace?.kind !== "campaign" || publishingDesign || discardingDraft) {
      return;
    }

    setPublishingDesign(true);

    try {
      const flushResult = await emailBuilderCommitAndFlushDraft({
        dispatch,
        getAutoSaveStatus: () => store.getState().emailBuilderStore.autoSaveStatus,
      getAutoSaveConflict: () => store.getState().emailBuilderStore.autoSaveConflict,
        isFlushing: isFlushingServerDraftRef.current,
        setFlushing: flushing => {
          isFlushingServerDraftRef.current = flushing;
        },
      });

      if (flushResult === "ignored") {
        return;
      }

      if (flushResult === "failed") {
        dispatch(showError(t("Could not finalize design.")));
        return;
      }

      const latestDraft = store.getState().emailBuilderStore.draft;
      if (latestDraft === null) {
        dispatch(showError(t("Could not finalize design.")));
        return;
      }

      const res = await PostEmailCampaignDesignPublish(
        ownerId,
        { revision: latestDraft.revision },
        false,
      );

      if (res.status === StatusCode.CONFLICT) {
        dispatchDraftRevisionConflict();
        return;
      }

      if (res.status !== StatusCode.OK || res.data?.data == null) {
        dispatch(
          showError(
            translateEmailBuilderValidationMessage(
              emailBuilderApiErrorMessage(res.message, t("Could not finalize design."), document),
              t,
            ),
          ),
        );
        return;
      }

      dismissFinalizeConfirm();
      dispatch(showSuccess(t("Design finalized.")));

      const campaignRes = await GetEmailCampaigns({ id: ownerId }, false);
      const loadedCampaign =
        campaignRes.status === StatusCode.OK && campaignRes.data.length > 0
          ? campaignRes.data[0]
          : null;
      const destination =
        loadedCampaign != null && isEmailCampaignDetailsReadyForSchedule(loadedCampaign)
          ? resolveEmailCampaignSchedulePathFromReturnTo(ownerId, location.state)
          : resolveEmailBuilderReturnTo(workspace, location.state);

      history.push(destination);
    } finally {
      setPublishingDesign(false);
    }
  }

  function handlePublishDesign() {
    if (ownerId === null || workspace?.kind !== "campaign" || publishingDesign || discardingDraft) {
      return;
    }

    runFinalizeReviewGate("finalizeDesign");
  }

  async function handleDiscardDraft() {
    if (ownerId === null || workspace?.kind !== "campaign" || discardingDraft || publishingDesign) {
      return;
    }

    setDiscardingDraft(true);

    try {
      emailBuilderCommitActiveEdits(dispatch);
      dispatch(emailBuilderCancelPendingAutosave());

      const res = await PostEmailCampaignDraftDiscard(ownerId, false);

      if (res.status !== StatusCode.OK) {
        dispatch(
          showError(
            translateEmailBuilderValidationMessage(
              emailBuilderApiErrorMessage(res.message, t("Could not discard draft."), document),
              t,
            ),
          ),
        );
        return;
      }

      setDiscardDraftOpen(false);

      if (res.data?.data == null) {
        dispatch(showSuccess(t("Draft discarded.")));
        history.push(emailCampaignSelectTemplatePath(ownerId), location.state);
        return;
      }

      dispatch(showSuccess(t("Draft restored from finalized design.")));
      await dispatch(emailBuilderLoad(workspace));
    } finally {
      setDiscardingDraft(false);
    }
  }

  function handlePrimaryAction() {
    if (isTemplateWorkspace) {
      void handleSaveTemplate();
      return;
    }

    void handlePublishDesign();
  }

  function shouldShowMobileAutosaveIndicator(): boolean {
    if (autoSaveConflict) {
      return true;
    }

    return (
      autoSaveStatus === "saving" ||
      autoSaveStatus === "unsaved-changes" ||
      autoSaveStatus === "error"
    );
  }

  function renderDraftStatusBadge() {
    if (autoSaveStatus === "saving") {
      return (
        <Badge type="gray" size="small" outline>
          <div className="email-builder-banner-saving" aria-hidden="true">
            {t("Saving…")} <Spin className="email-builder-banner-saving-spin" />
          </div>
        </Badge>
      );
    }

    return (
      <Badge type={draftStatusBadgeType} size="small" outline>
        <span className="email-builder-banner-draft-status" aria-hidden="true">
          {autoSaveStatus === "unsaved-changes" && typeof autoSaveDueAt === "number"
            ? t("Unsaved · {{seconds}}s", {
                seconds: emailBuilderUnsavedCountdownSeconds(autoSaveDueAt, nowMs),
              })
            : t(draftStatusLabel)}
        </span>
      </Badge>
    );
  }

  const showMobilePersistentStatusBadge = !isMobile || mode === "preview";
  const showMobileAutosaveIndicator =
    isMobile && mode === "design" && shouldShowMobileAutosaveIndicator();
  const bannerMetaLoading =
    loadStatus !== "succeeded" || campaign === null || draft === null;

  const primaryActionLabel = isTemplateWorkspace
    ? savingTemplate
      ? t("Saving…")
      : isMobile
        ? t("Save")
        : t("Save template")
    : publishingDesign
      ? t("Finalizing…")
      : isMobile
        ? t("Finalize")
        : t("Finalize design");

  return (
    <>
      <div
        className={classNames("email-builder-banner", {
          "email-builder-banner--preview": mode === "preview",
          "email-builder-banner--mobile": isMobile,
        })}
        style={{ zIndex: EMAIL_BUILDER_Z_LAYERS.shell.banner }}
        aria-busy={bannerMetaLoading}
      >
        <div
          className="email-builder-banner-status-live"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {liveAnnouncement}
        </div>
        <div className="email-builder-banner-content">
          <div className="email-builder-banner-start">
            <Button
              type="secondary"
              size="ellipsis"
              className="email-builder-banner-square-button"
              onClick={handleBackClick}
              aria-label={mode === "preview" ? t("Back to design") : t("Back")}
              icon={<FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />}
            />
          </div>
          <div className="email-builder-banner-center">
            {bannerMetaLoading ? (
              <>
                <EmailBuilderSkeleton
                  text
                  width={14}
                  className="email-builder-banner-loading-title"
                />
                <EmailBuilderSkeleton
                  text
                  width={7}
                  className="email-builder-banner-loading-status"
                />
              </>
            ) : (
              <>
                <div className="email-builder-banner-title">{campaign.name}</div>
                {mode === "preview" ? (
                  <Badge type="gray" size="small" outline>
                    <span className="email-builder-banner-preview-label" aria-hidden="true">
                      {t("Preview")}
                    </span>
                  </Badge>
                ) : showMobilePersistentStatusBadge ? (
                  renderDraftStatusBadge()
                ) : null}
              </>
            )}
          </div>
          <div className="email-builder-banner-end">
            {bannerMetaLoading ? (
              <>
                {!isMobile && (
                  <div className="email-builder-banner-loading-history" aria-hidden="true">
                    <EmailBuilderSkeleton className="email-builder-banner-loading-icon-button" />
                    <EmailBuilderSkeleton className="email-builder-banner-loading-icon-button" />
                  </div>
                )}
                {!isMobile && !isTemplateWorkspace && (
                  <EmailBuilderSkeleton className="email-builder-banner-loading-text-button email-builder-banner-loading-text-button--send-test" />
                )}
                <EmailBuilderSkeleton className="email-builder-banner-loading-text-button email-builder-banner-loading-text-button--publish" />
                <EmailBuilderSkeleton className="email-builder-banner-loading-square-button" />
              </>
            ) : (
              <>
                {mode === "preview" ? (
                  !isMobile && (
                    <div
                      className="email-builder-banner-viewport-options"
                      role="group"
                      aria-label={t("Preview viewport")}
                    >
                      {previewViewportOptions.map(option => {
                        const selected = option.viewport === previewViewport;

                        return (
                          <OverlayTrigger
                            key={option.viewport}
                            placement="bottom"
                            delay={{ show: 500, hide: 100 }}
                            overlay={
                              <Tooltip id={`email-builder-banner-viewport-tooltip-${option.viewport}`}>
                                {option.tooltip}
                              </Tooltip>
                            }
                          >
                            <button
                              type="button"
                              className={classNames("email-builder-banner-viewport-option", {
                                "email-builder-banner-viewport-option-selected": selected,
                              })}
                              aria-label={option.tooltip}
                              aria-pressed={selected}
                              onClick={() => dispatch(emailBuilderSetPreviewViewport(option.viewport))}
                            >
                              <FontAwesomeIcon
                                icon={option.viewport === "desktop" ? faDesktop : faMobile}
                                aria-hidden="true"
                              />
                            </button>
                          </OverlayTrigger>
                        );
                      })}
                    </div>
                  )
                ) : (
                  !isMobile && (
                    <div className="email-builder-banner-history-actions">
                  <OverlayTrigger
                    placement="bottom"
                    delay={{ show: 500, hide: 100 }}
                    overlay={
                      <Tooltip id="email-builder-banner-undo-tooltip">
                        {`${t("Undo")} (${shortcutLabels.undo})`}
                      </Tooltip>
                    }
                  >
                    <span>
                      <Button
                        type="text"
                        size="small"
                        className="email-builder-banner-icon-button"
                        disabled={!canUndo}
                        aria-label={`${t("Undo")} (${shortcutLabels.undo})`}
                        onClick={handleUndo}
                        icon={<FontAwesomeIcon icon={faRotateLeft} aria-hidden="true" />}
                      />
                    </span>
                  </OverlayTrigger>
                  <OverlayTrigger
                    placement="bottom"
                    delay={{ show: 500, hide: 100 }}
                    overlay={
                      <Tooltip id="email-builder-banner-redo-tooltip">
                        {`${t("Redo")} (${shortcutLabels.redo})`}
                      </Tooltip>
                    }
                  >
                    <span>
                      <Button
                        type="text"
                        size="small"
                        className="email-builder-banner-icon-button"
                        disabled={!canRedo}
                        aria-label={`${t("Redo")} (${shortcutLabels.redo})`}
                        onClick={handleRedo}
                        icon={<FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />}
                      />
                    </span>
                  </OverlayTrigger>
                </div>
              )
                )}
                {!isMobile && !isTemplateWorkspace && (
              <Button
                type="text"
                size="small"
                className="email-builder-banner-text-button"
                onClick={() => setSendTestOpen(true)}
              >
                <FontAwesomeIcon icon={faPaperPlane} aria-hidden="true" />
                <span>{t("Send test")}</span>
              </Button>
            )}
            <Button
              type="primary"
              size="small"
              className="email-builder-banner-publish-button"
              disabled={
                (isTemplateWorkspace && (savingTemplate || discardingTemplateDraft)) ||
                (!isTemplateWorkspace && (publishingDesign || discardingDraft))
              }
              onClick={handlePrimaryAction}
            >
              {primaryActionLabel}
            </Button>
            <div className="email-builder-banner-more-menu">
              <NavigationDropdownNew
                rightAlign
                hideChevron
                showPlainTextLabel
                onOpenChange={props.onMoreMenuOpenChange}
                label={
                  <Button
                    type="secondary"
                    size="ellipsis"
                    className="email-builder-banner-square-button"
                    aria-label={t("More actions")}
                    icon={<FontAwesomeIcon icon={faEllipsisVertical} aria-hidden="true" />}
                  />
                }
                sections={[
                  [
                    {
                      label: (
                        <span className="email-builder-banner-menu-option-label">
                          <FontAwesomeIcon icon={faEye} aria-hidden="true" />
                          {mode === "preview" ? t("Design") : t("Preview email")}
                        </span>
                      ),
                      onClick: () => {
                        void handlePreviewToggle();
                      },
                    },
                    {
                      label: (
                        <span className="email-builder-banner-menu-option-label">
                          <FontAwesomeIcon icon={faPaperPlane} aria-hidden="true" />
                          {t("Send test")}
                        </span>
                      ),
                      hidden: !isMobile || isTemplateWorkspace,
                      onClick: () => setSendTestOpen(true),
                    },
                    {
                      label: (
                        <span className="email-builder-banner-menu-option-label">
                          <FontAwesomeIcon icon={faRotateLeft} aria-hidden="true" />
                          {t("Undo")}
                        </span>
                      ),
                      hidden: !isMobile || mode === "preview",
                      disabled: !canUndo,
                      onClick: handleUndo,
                    },
                    {
                      label: (
                        <span className="email-builder-banner-menu-option-label">
                          <FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />
                          {t("Redo")}
                        </span>
                      ),
                      hidden: !isMobile || mode === "preview",
                      disabled: !canRedo,
                      onClick: handleRedo,
                    },
                    {
                      label: (
                        <span className="email-builder-banner-menu-option-label">
                          <FontAwesomeIcon icon={faFloppyDisk} aria-hidden="true" />
                          {t("Save as template")}
                        </span>
                      ),
                      hidden: isTemplateWorkspace,
                      disabled: ownerId === null || mode === "preview",
                      onClick: () => setSaveAsTemplateOpen(true),
                    },
                    {
                      label: (
                        <span className="email-builder-banner-menu-option-label">
                          <FontAwesomeIcon icon={faText} aria-hidden="true" />
                          {t("Rename template")}
                        </span>
                      ),
                      hidden: !isTemplateWorkspace,
                      disabled: ownerId === null || campaign === null || mode === "preview",
                      onClick: () => setRenameTemplateOpen(true),
                    },
                    {
                      label: (
                        <span className="email-builder-banner-menu-option-label">
                          <FontAwesomeIcon icon={faCode} aria-hidden="true" />
                          {exporting ? t("Exporting…") : t("Export as HTML")}
                        </span>
                      ),
                      disabled: exporting || ownerId === null,
                      onClick: () => {
                        void handleExportHtml();
                      },
                    },
                  ],
                  [
                    {
                      label: (
                        <span className="email-builder-banner-menu-option-label">
                          <FontAwesomeIcon icon={faTrash} aria-hidden="true" />
                          {t("Discard draft")}
                        </span>
                      ),
                      hidden: isTemplateWorkspace,
                      disabled:
                        ownerId === null ||
                        publishingDesign ||
                        discardingDraft ||
                        mode === "preview",
                      onClick: () => setDiscardDraftOpen(true),
                    },
                    {
                      label: (
                        <span className="email-builder-banner-menu-option-label">
                          <FontAwesomeIcon icon={faTrash} aria-hidden="true" />
                          {t("Discard draft")}
                        </span>
                      ),
                      hidden: !isTemplateWorkspace,
                      disabled:
                        ownerId === null ||
                        discardingTemplateDraft ||
                        savingTemplate ||
                        templateStatusKind === "published" ||
                        mode === "preview",
                      onClick: () => setDiscardDraftOpen(true),
                    },
                  ],
                ]}
              />
            </div>
              </>
            )}
          </div>
        </div>
        {showMobileAutosaveIndicator && !bannerMetaLoading ? (
          <div
            className="email-builder-banner-mobile-autosave-indicator"
            role="status"
            aria-live="polite"
          >
            {renderDraftStatusBadge()}
          </div>
        ) : null}
      </div>
      {!isTemplateWorkspace && (
        <>
          <EmailBuilderSendTestSheet open={sendTestOpen} onClose={() => setSendTestOpen(false)} />
          <EmailBuilderSaveAsTemplateModal
            open={saveAsTemplateOpen}
            onClose={() => setSaveAsTemplateOpen(false)}
            onSaved={() => setTemplateSavedAlertOpen(true)}
          />
        </>
      )}
      <EmailBuilderDiscardDraftModal
        open={discardDraftOpen}
        discarding={isTemplateWorkspace ? discardingTemplateDraft : discardingDraft}
        description={
          isTemplateWorkspace
            ? t("Discard this draft? Your changes will be reverted to the last saved version.")
            : t(
                "Discard this draft? If a finalized design exists, it will be restored. Otherwise you will return to template selection.",
              )
        }
        onClose={() => {
          if (!(isTemplateWorkspace ? discardingTemplateDraft : discardingDraft)) {
            setDiscardDraftOpen(false);
          }
        }}
        onConfirm={() => {
          if (isTemplateWorkspace) {
            void handleDiscardTemplateDraft();
          } else {
            void handleDiscardDraft();
          }
        }}
      />
      <EmailBuilderFinalizeConfirmModal
        open={finalizeConfirmOpen}
        confirming={publishingDesign || savingTemplate}
        review={finalizeReview}
        pendingAction={finalizePendingAction}
        onClose={() => {
          if (publishingDesign || savingTemplate) {
            return;
          }

          dismissFinalizeConfirm();
        }}
        onGoToBlock={handleFinalizeConfirmGoToBlock}
        onContinue={() => {
          if (finalizePendingAction === "finalizeDesign") {
            void finalizePublishDesign();
            return;
          }

          if (finalizePendingAction === "activateTemplate") {
            void finalizeActivateTemplate();
          }
        }}
      />
      {isTemplateWorkspace && ownerId !== null && campaign !== null ? (
        <EmailBuilderTemplateLibraryRenameModal
          open={renameTemplateOpen}
          template={{ id: ownerId, name: campaign.name }}
          onClose={() => setRenameTemplateOpen(false)}
          onRenamed={updated => {
            dispatch(emailBuilderSetCampaignName(updated.name));
            dispatch(showSuccess(t("Template renamed")));
          }}
        />
      ) : null}
      <TopAlert
        open={templateSavedAlertOpen}
        title={t("Template saved!")}
        description={
          isTemplateWorkspace
            ? t("New campaigns will use this version.")
            : t("You can now view it in your template library.")
        }
        closeLabel={t("Close")}
        onClose={() => setTemplateSavedAlertOpen(false)}
        action={{
          label: t("View template library"),
          onClick: () => {
            history.push(emailTemplateLibraryPath());
          },
        }}
      />
    </>
  );
}
